import * as R from 'remeda';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import {
  applyMiddleware,
  compose,
  createStore,
  Middleware,
  Store,
} from 'redux';
import {
  createReduxHistoryContext,
  LOCATION_CHANGE,
} from 'redux-first-history';
import { combineEpics } from 'redux-observable';
import { isEmbedded } from '../constants/config';
import {
  authDelete$,
  authGetBlob$,
  authGetJSON$,
  authPatch$,
  authPost$,
  authPostBlob$,
  authPut$,
} from '../lib/api';
import { initEmbeddedSsoSession } from '../lib/embeddedSsoSession';
import { State } from '../types/state';
import { createOptimizedEpicMiddleware } from './createOptimizedEpicMiddleware';
import embeddedMiddleware from './middlewares/embeddedMiddleware';
import localStorageMiddleware from './middlewares/localStorageMiddleware';
import messagesMiddleware from './middlewares/messagesMiddleware';
import metricsMiddleware from './middlewares/metricsMiddleware';
import queryClientMiddleware from './middlewares/queryClientMiddleware';
import timeSpentMiddleware from './middlewares/timeSpentMiddleware';
import trackingMiddleware from './middlewares/trackingMiddleware';
import createRootReducer from './modules';
import annotationEpics from './modules/annotation/epics';
import annotationsEpic from './modules/annotations/epics';
import authEpic from './modules/auth/epics';
import bboxesEpics from './modules/bboxes/epics';
import beforeLeaveEpics from './modules/beforeLeave/epics';
import datapointsEpics from './modules/datapoints/epics';
import suggestedOperationsEpics from './modules/datapoints/suggestedOperations/epics';
import emailEpics from './modules/email/epics';
import emailsEpics from './modules/emails/epics';
import gridEpics from './modules/grid/epics';
import groupsEpics from './modules/groups/epics';
import inboxEpics from './modules/inbox/epics';
import localeEpics from './modules/locale/epics';
import messagesEpics from './modules/messages/epics';
import organizationEpics from './modules/organization/epics';
import organizationGroupEpics from './modules/organizationGroup/epics';
import popupEpics from './modules/popup/epics';
import productTourEpics from './modules/productTour/epics';
import reportEpics from './modules/report/epics';
import schemaEpics from './modules/schema/epics';
import searchEpics from './modules/search/epics';
import stackEpics from './modules/stack/epics';
import statisticsEpics from './modules/statistics/epics';
import trackingObserver from './modules/tracking/stateObservers';
import uiEpics from './modules/ui/epics';
import userEpics from './modules/user/epics';
import { RootActionType } from './rootActions';

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({ history: createBrowserHistory() });

const composeEnhancers =
  (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      maxAge: 50,
      trace: false,
    })) ||
  compose;

const epicMiddleware = createOptimizedEpicMiddleware({
  dependencies: {
    authDelete$,
    authGetBlob$,
    authGetJSON$,
    authPatch$,
    authPost$,
    authPostBlob$,
    authPut$,
  },
});

const rootEpic = combineEpics(
  annotationEpics,
  annotationsEpic,
  authEpic,
  bboxesEpics,
  datapointsEpics,
  emailEpics,
  emailsEpics,
  gridEpics,
  groupsEpics,
  inboxEpics,
  localeEpics,
  messagesEpics,
  organizationEpics,
  organizationGroupEpics,
  reportEpics,
  schemaEpics,
  searchEpics,
  stackEpics,
  statisticsEpics,
  suggestedOperationsEpics,
  trackingObserver,
  uiEpics,
  userEpics,
  productTourEpics,
  popupEpics,
  beforeLeaveEpics
);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // TODO: We can use stateTransformer to only log parts of the state we are interested in
  // but first we have to know what those are, otherwise this is just wasting attachment allocation
  attachReduxState: false,
});

const configureStore = () => {
  const middlewares = R.pipe<(Middleware | undefined)[], Middleware[]>(
    [
      messagesMiddleware,
      isEmbedded() ? embeddedMiddleware : undefined,
      routerMiddleware,
      epicMiddleware,
      timeSpentMiddleware,
      metricsMiddleware,
      trackingMiddleware,
      localStorageMiddleware,
      queryClientMiddleware,
    ],
    R.filter(R.isTruthy)
  );

  const _store: Store<State, RootActionType> = createStore(
    createRootReducer(routerReducer),
    composeEnhancers(applyMiddleware(...middlewares), sentryReduxEnhancer)
  );

  return _store;
};

// Pin the iframe-embedded autologin SSO provider for this tab BEFORE the store
// is created. The initial LOCATION_CHANGE dispatched below can trigger
// enterLoginEpic to redirect away from `/` (when a leftover token exists),
// which strips the `auto_login_sso_provider_id` query before we could read it.
initEmbeddedSsoSession();

const store = configureStore();
window.store = store;

export const history = createReduxHistory(store);

// this have to be started AFTER the store has been created
epicMiddleware.run(rootEpic);

// Emit location action on load - for backwards compatibility
// TODO refactor.
store.dispatch({
  type: LOCATION_CHANGE,
  payload: store.getState().router,
});

export default store;
